import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import ReactJson from 'react-json-view'

const LanguageJsonUploadView = props => {
  const { langJson, getLangJson, updateLangJson } = props
  const [fileName, setFileName] = useState('')
  const [fileData, setFileData] = useState(null) // { lang: { key: value } }
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  // DB row.data가 문자열로 내려오는 경우까지 대비해 객체로 정규화
  const existingByLang = useMemo(() => {
    const result = {}
    langJson.forEach(row => {
      try {
        result[row.lang] =
          typeof row.data === 'string' ? JSON.parse(row.data) : row.data
      } catch (err) {
        result[row.lang] = {}
      }
    })
    return result
  }, [langJson])

  const rowByLang = useMemo(() => {
    const result = {}
    langJson.forEach(row => {
      result[row.lang] = row
    })
    return result
  }, [langJson])

  const diffs = useMemo(() => {
    if (!fileData) return []

    const langs = Array.from(
      new Set([...Object.keys(existingByLang), ...Object.keys(fileData)])
    ).sort()

    return langs.map(lang => {
      const oldMap = existingByLang[lang]
      const newMap = fileData[lang]

      if (!newMap) return { lang, status: 'notInFile' }
      if (!oldMap) return { lang, status: 'noRow' }

      const added = []
      const removed = []
      const changed = []

      Object.keys(newMap).forEach(key => {
        if (!(key in oldMap)) {
          added.push({ key, after: newMap[key] })
        } else if (
          JSON.stringify(oldMap[key]) !== JSON.stringify(newMap[key])
        ) {
          changed.push({ key, before: oldMap[key], after: newMap[key] })
        }
      })
      Object.keys(oldMap).forEach(key => {
        if (!(key in newMap)) {
          removed.push({ key, before: oldMap[key] })
        }
      })

      return { lang, status: 'ok', added, removed, changed }
    })
  }, [fileData, existingByLang])

  const changedDiffs = diffs.filter(
    d =>
      d.status === 'ok' &&
      d.added.length + d.removed.length + d.changed.length > 0
  )

  const countKeys = obj =>
    obj && typeof obj === 'object' ? Object.keys(obj).length : 0

  const fmt = value => {
    const str = typeof value === 'string' ? value : JSON.stringify(value)
    return str && str.length > 120 ? `${str.slice(0, 120)}…` : str
  }

  const resetFile = () => {
    setFileName('')
    setFileData(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const onSelectFile = e => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result)
        // { data: { ko: {...} } } 형태와 { ko: {...} } 형태 모두 허용
        const data =
          json && typeof json.data === 'object' && json.data !== null
            ? json.data
            : json

        if (
          !data ||
          typeof data !== 'object' ||
          Array.isArray(data) ||
          Object.keys(data).length === 0 ||
          Object.values(data).some(v => typeof v !== 'object' || v === null)
        ) {
          throw new Error('invalid structure')
        }

        setFileData(data)
        setFileName(file.name)
      } catch (err) {
        alert(
          '올바른 번역 JSON 파일이 아닙니다.\n{ "data": { "ko": { ... }, "en": { ... } } } 형태의 파일을 업로드해주세요.'
        )
        resetFile()
      }
    }
    reader.readAsText(file)
  }

  const onClickDownload = () => {
    const dateStr = new Date()
      .toISOString()
      .slice(2, 10)
      .replace(/-/g, '')
    const content = JSON.stringify({ data: existingByLang }, null, 2)
    const blob = new Blob([content], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = `translations_${dateStr}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const onClickComplete = async () => {
    if (changedDiffs.length === 0) {
      alert('변경 사항이 없습니다.')
      return
    }

    const summary = changedDiffs
      .map(
        d =>
          `- ${d.lang}: 추가 ${d.added.length} / 삭제 ${d.removed.length} / 변경 ${d.changed.length}`
      )
      .join('\n')

    if (
      !window.confirm(
        `아래 언어의 번역 데이터를 업데이트합니다.\n${
          changedDiffs.length
        }개 언어 변경: ${changedDiffs
          .map(d => d.lang)
          .join(', ')}\n\n${summary}\n\n계속하시겠습니까?`
      )
    ) {
      return
    }

    setUploading(true)

    const failed = []
    for (const d of changedDiffs) {
      const row = rowByLang[d.lang]
      const success = await updateLangJson(row.seq, fileData[d.lang])
      if (!success) failed.push(d.lang)
    }

    setUploading(false)

    if (failed.length > 0) {
      alert(`일부 언어 업데이트에 실패했습니다: ${failed.join(', ')}`)
    } else {
      alert('업데이트가 완료되었습니다.')
      resetFile()
    }
    getLangJson()
  }

  const renderDiffSection = () => (
    <DiffSection>
      <SectionTitle>변경 사항</SectionTitle>
      <SectionSummary>
        {changedDiffs.length > 0
          ? `${changedDiffs.length}개 언어 변경: ${changedDiffs
              .map(d => d.lang)
              .join(', ')}`
          : '변경된 언어가 없습니다.'}
      </SectionSummary>
      {diffs.map(d => {
        if (d.status === 'notInFile') {
          return (
            <DiffLangBox key={d.lang}>
              <DiffLangHeader>
                <LangCode>{d.lang}</LangCode>
                <SkipText>파일에 없는 언어입니다. 변경하지 않습니다.</SkipText>
              </DiffLangHeader>
            </DiffLangBox>
          )
        }

        if (d.status === 'noRow') {
          return (
            <DiffLangBox key={d.lang}>
              <DiffLangHeader>
                <LangCode>{d.lang}</LangCode>
                <SkipText>
                  DB에 해당 언어 데이터가 없어 건너뜁니다. (신규 언어 추가는
                  지원하지 않습니다)
                </SkipText>
              </DiffLangHeader>
            </DiffLangBox>
          )
        }

        const total = d.added.length + d.removed.length + d.changed.length

        if (total === 0) {
          return (
            <DiffLangBox key={d.lang}>
              <DiffLangHeader>
                <LangCode>{d.lang}</LangCode>
                <SkipText>변경 사항 없음</SkipText>
              </DiffLangHeader>
            </DiffLangBox>
          )
        }

        return (
          <DiffLangBox key={d.lang}>
            <details open={total <= 30}>
              <summary style={{ cursor: 'pointer' }}>
                <LangCode>{d.lang}</LangCode>
                <CountText color="#00a65a">추가 {d.added.length}</CountText>
                <CountText color="#dd4b39">삭제 {d.removed.length}</CountText>
                <CountText color="#f39c12">변경 {d.changed.length}</CountText>
              </summary>
              <DiffList>
                {d.added.map(item => (
                  <DiffItem key={`a-${item.key}`}>
                    <Badge color="#00a65a">추가</Badge>
                    <DiffKey>{item.key}</DiffKey>
                    <DiffValue>"{fmt(item.after)}"</DiffValue>
                  </DiffItem>
                ))}
                {d.removed.map(item => (
                  <DiffItem key={`r-${item.key}`}>
                    <Badge color="#dd4b39">삭제</Badge>
                    <DiffKey>{item.key}</DiffKey>
                    <DiffValue style={{ textDecoration: 'line-through' }}>
                      "{fmt(item.before)}"
                    </DiffValue>
                  </DiffItem>
                ))}
                {d.changed.map(item => (
                  <DiffItem key={`c-${item.key}`}>
                    <Badge color="#f39c12">변경</Badge>
                    <DiffKey>{item.key}</DiffKey>
                    <DiffValue>
                      "{fmt(item.before)}"{' '}
                      <Arrow>→</Arrow> "{fmt(item.after)}"
                    </DiffValue>
                  </DiffItem>
                ))}
              </DiffList>
            </details>
          </DiffLangBox>
        )
      })}
    </DiffSection>
  )

  return (
    <div style={{ minHeight: '100%' }}>
      <section className="content-header">
        <h1>Language JSON 파일 업로드</h1>
        <ol className="breadcrumb">
          <li>
            <a href="/LanguageUpload">
              <i className="fa fa-dashboard"></i>Language Json Upload
            </a>
          </li>
        </ol>
      </section>
      <section className="content">
        <div className="row">
          <section className="col-lg-12 connectedSortable">
            <div className="box box-info">
              <div
                className="box-header with-border"
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={onSelectFile}
                  style={{ marginBottom: 0 }}
                />
                {fileData && (
                  <button className="btn btn-default" onClick={resetFile}>
                    취소
                  </button>
                )}
                <button
                  className="btn btn-default"
                  style={{ marginLeft: 'auto' }}
                  disabled={langJson.length === 0}
                  onClick={onClickDownload}
                >
                  기존 언어파일 받기
                </button>
              </div>
              <div className="box-body">
                <Row>
                  <Col>
                    <ColTitle>현재 데이터</ColTitle>
                    {langJson.length > 0 ? (
                      <>
                        <MetaChips>
                          {langJson.map(row => (
                            <LangChip key={row.lang}>
                              {row.lang} · {countKeys(existingByLang[row.lang])}
                              개
                            </LangChip>
                          ))}
                        </MetaChips>
                        <JsonBox>
                          <ReactJson
                            src={existingByLang}
                            collapsed={1}
                            name={null}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            quotesOnKeys={false}
                          />
                        </JsonBox>
                      </>
                    ) : (
                      <Meta>데이터를 불러오는 중입니다...</Meta>
                    )}
                  </Col>
                  <Col>
                    <ColTitle>
                      업로드할 파일 {fileName && `(${fileName})`}
                    </ColTitle>
                    {fileData ? (
                      <>
                        <MetaChips>
                          {Object.keys(fileData).map(lang => (
                            <LangChip key={lang}>
                              {lang} · {countKeys(fileData[lang])}개
                            </LangChip>
                          ))}
                        </MetaChips>
                        <JsonBox>
                          <ReactJson
                            src={fileData}
                            collapsed={1}
                            name={null}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            quotesOnKeys={false}
                          />
                        </JsonBox>
                      </>
                    ) : (
                      <Meta>JSON 파일을 선택하면 미리보기가 표시됩니다.</Meta>
                    )}
                  </Col>
                </Row>

                {fileData && renderDiffSection()}

                {fileData && (
                  <CompleteWrapper>
                    <button
                      className="btn btn-primary btn-lg"
                      disabled={uploading || changedDiffs.length === 0}
                      onClick={onClickComplete}
                    >
                      {uploading
                        ? '업데이트 중...'
                        : changedDiffs.length === 0
                        ? '변경 사항 없음'
                        : '완료'}
                    </button>
                  </CompleteWrapper>
                )}
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>
  )
}

export default LanguageJsonUploadView

const Row = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`

const Col = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const ColTitle = styled.div`
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 5px;
`

const Meta = styled.div`
  font-size: 13px;
  color: #777;
  margin-bottom: 8px;
  word-break: break-all;
`

const MetaChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`

const LangChip = styled.span`
  font-size: 12px;
  color: #555;
  background-color: #f0f0f0;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  padding: 1px 8px;
  white-space: nowrap;
`

const JsonBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px;
  max-height: 500px;
  overflow: auto;
  word-break: break-all;
`

const DiffSection = styled.div`
  margin-top: 25px;
`

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
`

const SectionSummary = styled.div`
  font-size: 13px;
  color: #555;
  margin-bottom: 10px;
`

const DiffLangBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px 15px;
  margin-bottom: 8px;
`

const DiffLangHeader = styled.div`
  display: flex;
  align-items: center;
`

const LangCode = styled.span`
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  margin-right: 12px;
  min-width: 30px;
`

const SkipText = styled.span`
  font-size: 13px;
  color: #999;
`

const CountText = styled.span`
  font-size: 13px;
  font-weight: bold;
  color: ${props => props.color};
  margin-right: 10px;
`

const DiffList = styled.div`
  margin-top: 10px;
  max-height: 400px;
  overflow: auto;
`

const DiffItem = styled.div`
  display: flex;
  align-items: baseline;
  font-size: 13px;
  padding: 3px 0;
  border-bottom: 1px solid #f5f5f5;
`

const Badge = styled.span`
  display: inline-block;
  flex-shrink: 0;
  background-color: ${props => props.color};
  color: #fff;
  font-size: 11px;
  border-radius: 3px;
  padding: 1px 6px;
  margin-right: 8px;
`

const DiffKey = styled.span`
  flex-shrink: 0;
  font-weight: bold;
  margin-right: 10px;
  word-break: break-all;
`

const DiffValue = styled.span`
  color: #555;
  word-break: break-all;
  white-space: pre-wrap;
`

const Arrow = styled.span`
  color: #f39c12;
  font-weight: bold;
`

const CompleteWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`
